"use client";

import { useEffect, useState } from "react";
import { Product } from "@/types/product";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { SAMPLE_NAMES } from "@/data/sample-names";

interface PurchaseNotificationProps {
	products: { products: Product[] };
}

export default function PurchaseNotification({
	products,
}: PurchaseNotificationProps) {
	const [showNotification, setShowNotification] = useState(false);
	const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
	const [currentName, setCurrentName] = useState("");

	useEffect(() => {
		// Function to show a random purchase notification
		const showRandomPurchase = () => {
			if (!products.products || products.products.length === 0) return;

			// Filter products that are in stock
			const inStockProducts = products.products.filter(
				(product) => product.stock > 0
			);

			// If no products are in stock, don't show notification
			if (inStockProducts.length === 0) return;

			const randomProduct =
				inStockProducts[Math.floor(Math.random() * inStockProducts.length)];
			const randomName =
				SAMPLE_NAMES[Math.floor(Math.random() * SAMPLE_NAMES.length)];

			setCurrentProduct(randomProduct);
			setCurrentName(randomName);
			setShowNotification(true);

			// Hide notification after 8 seconds
			setTimeout(() => {
				setShowNotification(false);
			}, 8000);

			// Save the next notification time to localStorage
			const nextNotificationTime =
				Date.now() + (Math.random() * 10 + 20) * 60 * 1000; // 20-30 minutes
			localStorage.setItem("nt_ts_z1aQw", nextNotificationTime.toString());
		};

		// Check if we should show the first notification
		const checkFirstNotification = () => {
			const lastNotificationTime = localStorage.getItem("nt_lstx_pq21!z");
			const nextNotificationTime = localStorage.getItem("nt_ts_z1aQw");

			if (!lastNotificationTime) {
				// First time visit - show notification after 4-8 minutes
				const initialDelay = (Math.random() * 4 + 4) * 60 * 1000; // 4-8 minutes
				setTimeout(() => {
					showRandomPurchase();
					localStorage.setItem("nt_lstx_pq21!z", Date.now().toString());
				}, initialDelay);
			} else if (nextNotificationTime) {
				const nextTime = parseInt(nextNotificationTime);
				const now = Date.now();

				if (now >= nextTime) {
					// It's time for the next notification
					showRandomPurchase();
					localStorage.setItem("nt_lstx_pq21!z", now.toString());
				} else {
					// Schedule the next notification
					const delay = nextTime - now;
					setTimeout(() => {
						showRandomPurchase();
						localStorage.setItem("nt_lstx_pq21!z", Date.now().toString());
					}, delay);
				}
			}
		};

		// Initialize notification timing
		checkFirstNotification();

		// Cleanup function
		return () => {
			// No need to clear timeouts as they're managed by localStorage
		};
	}, [products]);

	const handleClose = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		setShowNotification(false);
	};

	return (
		<AnimatePresence>
			{showNotification && currentProduct && (
				<motion.div
					initial={{ opacity: 0, y: 50, scale: 0.3 }}
					animate={{ opacity: 1, y: 0, scale: 1 }}
					exit={{ opacity: 0, scale: 0.5, transition: { duration: 0.2 } }}
					className="fixed bottom-4 right-4 z-50"
				>
					<Link
						href={`/products/${currentProduct.slug}`}
						className="block transition-transform hover:scale-[1.02] active:scale-[0.98]"
					>
						<div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 max-w-sm border border-gray-200 dark:border-gray-700 relative">
							<button
								onClick={handleClose}
								className="absolute -top-2 -right-2 w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors border border-gray-200 dark:border-gray-600 shadow-sm"
							>
								<svg
									className="w-4 h-4"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
							</button>
							<div className="flex items-start space-x-3">
								<div className="flex-shrink-0">
									<div className="w-12 h-12 rounded-lg overflow-hidden relative">
										{currentProduct.images && currentProduct.images[0] && (
											<Image
												src={currentProduct.images[0]}
												alt={currentProduct.name}
												fill
												className="object-cover"
												sizes="48px"
											/>
										)}
									</div>
								</div>
								<div className="flex-1">
									<div className="flex items-center space-x-2">
										<div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
										<p className="text-sm font-medium text-gray-900 dark:text-white">
											{currentName} just purchased
										</p>
									</div>
									<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
										{currentProduct.name}
									</p>
									<p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
										{Math.floor(Math.random() * 30) + 1} minutes ago
									</p>
								</div>
							</div>
						</div>
					</Link>
				</motion.div>
			)}
		</AnimatePresence>
	);
}
